import { useInvoiceCommands } from "@/shared/useInvoiceCommands";
import { usePrivateToast } from "@/shared/usePrivateToast";
import { invalidateInvoiceMutation } from "@/shared/invalidation";
import { countedKey } from "@/lib/i18n/plural";
import { useEffect, useMemo, useState } from "react";
import {
  Link,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/lib/api/endpoints";
import { useTranslate } from "@/lib/i18n";
import {
  queryKeys,
  useApiQuery,
} from "@/shared/queries";
import { useErrorMessage } from "@/shared/errors";
import { Button, ErrorState, LoadingSkeleton } from "@a2z/ui";
import { ListCard, Screen, useMoney } from "@/shared/ui";
import { DrawerStrip } from "@/modules/pos/components/DrawerStrip";
import { useLocalName } from "@/shared/localName";

import { hasPermission, PERMISSIONS, useSession } from "@/modules/auth/useSession";
import type {
  AddonGroup,
  Category,
  MenuItem,
  PosPriceTier,
  RestaurantSettings,
  StaffMember,
  TableGridArea,
} from "@/lib/api/types";
import { TillSummaryBar } from "@/modules/pos/components/TillSummaryBar";
import { TillPhoneLead } from "@/modules/pos/components/TillPhoneLead";
import { canSendToKitchen } from "@/modules/pos/lib/kitchenRouting";
import type { TillPane } from "@/modules/pos/lib/initialPane";
import type { InvoiceHandoff } from "@/modules/pos/pages/InvoicePage";
import { MenuBoard } from "@/modules/pos/components/MenuBoard";
import { useMenuAvailability } from "@/modules/pos/useMenuAvailability";
import { AddonPicker } from "@/modules/pos/components/AddonPicker";
import { Modal } from "@a2z/ui";
import {
  handoffDecision,
  quoteFromPrice,
  resolveGroupsForItem,
} from "@/modules/pos/lib/pricing";
import {
  PaperRegisterTabs,
  PaperTillBarLeading,
  SECTION_PARAM,
} from "@/modules/pos/components/PaperRegister";
import { usePaperTill } from "@/shared/posTheme";
import { IngredientWarningDialog } from "@/modules/pos/components/IngredientWarningDialog";
import { OrderPanel, type PanelTable } from "@/modules/pos/components/OrderPanel";
import { formatTierWindow } from "@/shared/tierWindow";
import { resolveOpenInvoicePayload } from "@/modules/pos/lib/openInvoicePayload";
import {
  GUEST_COUNT_CHIP_VALUES,
  toggleGuestCountChip,
} from "@/modules/pos/lib/guestCountChips";
import { NO_CLOSE_BLOCKING } from "@/modules/pos/lib/tillActions";
import { GuestCountDialog } from "@/modules/pos/components/GuestCountDialog";
import { CaptainChips } from "@/modules/pos/components/CaptainChips";
import {
  captainChips,
  isWaiterSession,
  shownCaptain,
  toggleCaptainChip,
} from "@/modules/pos/lib/captain";

/**
 * A table that has no open invoice yet — the step that used to not exist.
 *
 * Tapping a table on the floor used to `openInvoice` on the spot, so a mis-tap
 * seated a customer: the tile went occupied, an invoice number was burned, and
 * undoing it meant cancelling a real invoice.
 *
 * The table now goes occupied on the FIRST ITEM, not on the tap that got here.
 * This screen is the order view before that item exists: the same two panes,
 * the same live menu, an order panel that has nothing in it yet. Picking an
 * item opens the invoice and continues on the order view mid-gesture — there
 * is no "start" step to remember, because the first thing a waiter does at a
 * table is the start.
 *
 * If the table turns out to be occupied — someone else opened it, or this is a
 * stale ordinary link — this redirects to that invoice. The explicit new-party
 * action keeps the pre-order screen so another bill can be started deliberately.
 */
export function TablePage() {
  const { dispatchCommand } = useInvoiceCommands();
  const toast = usePrivateToast();
  const t = useTranslate();
  const errorMessage = useErrorMessage();
  const { tableId = "", locale = "ar" } = useParams();
  const money = useMoney();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // PAPER (light till): the register's two steps, its tabs and the till's bar.
  const paper = usePaperTill();
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const availability = useMenuAvailability();
  const [isOpening, setOpening] = useState(false);
  /**
   * The tier the NEXT order opens with, and what that choice means.
   *
   * `priceTierId` on the wire is tri-state (contract amendment to E10, tier-2
   * P2 rework): a store can have an auto-applying happy hour, and a staff
   * open that never touches the picker sends NO `priceTierId` at all, which
   * lets the SERVER pick the in-window auto tier. `tierTouched` is what
   * separates that "the waiter has not decided" state — where the auto tier
   * is only pre-selected as a DISPLAY default below, nothing is sent yet —
   * from "the waiter deliberately chose a chip, including Base prices",
   * which sends the chip's value verbatim: `null` for Base prices, so the
   * server never auto-applies over a choice the UI showed being made.
   */
  const [tierId, setTierId] = useState<string | null>(null);
  /**
   * B18 — the same warning the invoice grid gives, on the tap that OPENS the
   * bill.
   *
   * Here for the same reason it is there and not a different one: this tap
   * also ends with the dish on a bill (the item is handed off and appended on
   * the invoice screen), so a waiter who came in through the table view would
   * otherwise be the one person never told. The dialog refuses nothing — its
   * primary action opens the bill exactly as the bare tap would.
   */
  const [warnTarget, setWarnTarget] = useState<MenuItem | null>(null);

  const [tierTouched, setTierTouched] = useState(false);
  /**
   * D45 (S5) / covers rework Phase 2a: covers, as free text so BLANK stays a
   * real answer.
   *
   * Answered through the chips below (`guestCountField`) rather than a
   * pre-filled stepper: nothing is preselected on load, so a table whose
   * waiter skips the question sends no field at all, and the reports cannot
   * tell an invented cover from a counted one. Optional means optional.
   */
  const [guestCount, setGuestCount] = useState("");
  /** Covers rework Phase 2a — «أكتر» opens the same dialog the invoice
   *  screen's covers row uses, for a count past the six quick chips. */
  const [guestCountDialogOpen, setGuestCountDialogOpen] = useState(false);
  /**
   * D153 — «الكابتن», asked here beside the covers, on the same inline-chip
   * pattern. `captainTouched` separates "nobody tapped" — where the SERVER
   * applies the default (the opener, only when this is a waiter's session)
   * and nothing is sent — from a deliberate choice, `null` included.
   */
  const [captainTouched, setCaptainTouched] = useState(false);
  const [captainChosen, setCaptainChosen] = useState<string | null>(null);
  // The dish's name in the language the till is in — the picker's own title.
  const localName = useLocalName();

  /* Which pane a phone shows (see the layout below); `null` = not asked yet. */
  const [paneOverride, setPaneOverride] = useState<TillPane | null>(null);
  /* Only for the bar's Send label: kitchen, or the cashier (D120). */
  const settingsQuery = useApiQuery<RestaurantSettings>(
    queryKeys.settings,
    endpoints.restaurant.settings,
    { retry: false },
  );
  const gridQuery = useApiQuery<TableGridArea[]>(
    queryKeys.grid,
    endpoints.restaurant.grid,
    { refetchOnWindowFocus: true },
  );
  const menuQuery = useApiQuery<MenuItem[]>(
    queryKeys.menuItems,
    endpoints.restaurant.menuItems,
    // B117/Q9 — the till has to notice an 86 without being told. See
    // Availability updates arrive through the shared Firestore revision listener.
    { refetchOnWindowFocus: true },
  );
  const categoriesQuery = useApiQuery<Category[]>(
    queryKeys.categories,
    endpoints.seller.categories,
  );

  /**
   * Hidden sections do not exist at the till.
   *
   * Same filter as `InvoicePage`, and for the same reason: the back-office
   * listing must keep returning hidden sections — the Sections screen is the
   * only place one can be turned back on — so the till drops them itself. The
   * public storefront listing filters on the server, where there is no such
   * need.
   */
  const visibleCategories = useMemo(
    () => (categoriesQuery.data ?? []).filter((category) => category.isActive),
    [categoriesQuery.data],
  );

  /**
   * THE ADDON GROUPS, READ HERE — which is the whole of the fix (B108).
   *
   * The first tap on a menu tile is what OPENS THE BILL, and until now it did
   * that first and asked about the add-ons afterwards, on the next screen. So
   * tapping a dish that turns out to need a size, then backing out of the
   * picker, left a real invoice behind: a table reading occupied, a 0 EGP bill,
   * and a daily sequence burnt on a plate nobody ordered. A mis-tap on the
   * busiest screen in the product had a permanent consequence.
   *
   * The picker now runs HERE, before any command goes out, and the invoice is
   * opened only once the waiter has confirmed it. That needs the groups on this
   * screen, which is also the cache `handoffDecision`'s own doc records as
   * never having been populated by this page — the reason the hand-off had to
   * carry a WAIT state at all.
   */
  const addonGroupsQuery = useApiQuery<AddonGroup[]>(
    queryKeys.addonGroups,
    endpoints.restaurant.addonGroups,
  );

  /**
   * The dish whose picker is open, before the bill exists.
   *
   * `null` on every ordinary tap of a plain dish — that path still opens the
   * bill straight away, because there is nothing to ask.
   */
  const [pickerItem, setPickerItem] = useState<MenuItem | null>(null);

  const canOpen = hasPermission(session, PERMISSIONS.INVOICE_OPEN);

  /*
   * D153 — the chip row's members: `GET /restaurant/staff` (POS_USE), the
   * same money-free list the drawer's counterparty picker reads, which says
   * who holds the built-in Waiter role and in what order to offer them.
   */
  const staffQuery = useApiQuery<StaffMember[]>(
    queryKeys.restaurantStaff,
    endpoints.restaurant.staff,
    { enabled: canOpen },
  );
  const captainOptions = captainChips(staffQuery.data ?? []);
  /*
   * A waiter's session may not answer the question — not a colleague's name,
   * not "nobody" (the server refuses both with `SELLER_PERMISSION_DENIED`) —
   * and does not choose the pricing either (2026-10-28, founder: «the waiter
   * can't control the waiter or the pricing, he only sees the menu»). Neither
   * row is drawn for one: the bill opens with no captain and no tier field, so
   * the server makes him the captain and applies the in-window auto tier, and
   * the menu's prices still follow that tier (`effectiveTierId`).
   */
  const waiterSession = isWaiterSession(session?.seller.permissions ?? []);
  const shownCaptainId = shownCaptain({
    touched: captainTouched,
    chosen: captainChosen,
    sessionSellerId: session?.seller.id ?? null,
    permissions: session?.seller.permissions ?? [],
    chips: captainOptions,
  });

  /*
   * The tier is chosen HERE because it is captured at open and never
   * re-resolved (the invoice snapshots it): once the first item lands, the
   * pricing is decided. Backend support existed since phase 1 — this picker is
   * what finally makes the feature reachable from a till.
   */
  const tiersQuery = useApiQuery<PosPriceTier[]>(
    queryKeys.posPriceTiers,
    endpoints.restaurant.posPriceTiers,
    { enabled: canOpen, retry: false },
  );
  const tiers = tiersQuery.data ?? [];
  /**
   * The tier this open would use if the waiter touches nothing (D47, B4).
   *
   * `isActiveNow` is the SERVER's verdict against the STORE timezone — never
   * recomputed here — and the first in-window auto-apply tier is the same one
   * `resolveTierForOpenInTx` would choose, so the picker shows the price the
   * bill will actually open at instead of quietly disagreeing with it.
   */
  const autoTier = tiers.find((tier) => tier.autoApply && tier.isActiveNow) ?? null;
  const effectiveTierId = tierTouched ? tierId : (autoTier?.id ?? null);

  const tables = (gridQuery.data ?? []).flatMap((area) => area.tables);
  const table = tables.find((entry) => entry.tableId === tableId);

  /**
   * The redirect below is a decision made ON CACHED DATA, and the cache this
   * screen reads is the floor's — refreshed when a floor revision arrives
   * and held fresh for 5s by the query client. So a table whose invoice was
   * settled seconds ago can still carry an `openInvoice` here, and the waiter
   * who taps it to start the next order lands on a settled bill with no way
   * forward. (The mirror case is harmless: a stale FREE table only means the
   * server refuses the open with the mapped `RESTAURANT_TABLE_OCCUPIED`.)
   *
   * So: ask for a fresh grid on arrival, and only bounce off data that fetch
   * produced. The wait costs a skeleton on an occupied table and nothing at
   * all on a free one — the common case is untouched.
   */
  // State, not a ref: this is read during render (it decides what the screen
  // shows), and the lazy initialiser is what keeps `Date.now()` out of the
  // render path on every pass.
  const [mountedAt] = useState(() => Date.now());
  useEffect(() => {
    void gridQuery.refetch();
    // Once, on arrival. `gridQuery` is a fresh object every render, so listing
    // it here would refetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isGridFresh =
    gridQuery.dataUpdatedAt >= mountedAt ||
    gridQuery.errorUpdatedAt >= mountedAt;

  const backLink = (
    <Link
      to={`/${locale}/pos`}
      aria-label={t((m) => m.pos.backToTables)}
      title={t((m) => m.pos.backToTables)}
      data-ui="icon-button"
      className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[var(--radius-tile)] border border-[color:var(--border)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--surface-alt)] hover:text-[color:var(--text)]"
      data-testid="table-back-to-tables"
    >
      <svg
        width="17"
        height="17"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="rtl:-scale-x-100"
      >
        <path d="M19 12H5M12 19l-7-7 7-7" />
      </svg>
    </Link>
  );

  if (
    gridQuery.isLoading ||
    // The cached grid says this table is taken: wait for the fetch this visit
    // started before acting on that, rather than bouncing onto an invoice that
    // may already have been settled.
    (Boolean(table?.openInvoices.length) && !isGridFresh && !isOpening)
  ) {
    return (
      <Screen tone="dark" title={t((m) => m.common.loading)} leading={backLink}>
        <div
          aria-busy="true"
          aria-live="polite"
          aria-label={t((m) => m.common.loading)}
        >
          <LoadingSkeleton variant="detail" rows={4} />
        </div>
      </Screen>
    );
  }

  if (!table) {
    return (
      <Screen tone="dark" title={t((m) => m.common.error)} leading={backLink}>
        <ErrorState
          // `errorMessage`, not `error.message`: a dropped connection would
          // otherwise show the browser's English "Failed to fetch" here.
          message={
            gridQuery.error
              ? errorMessage(gridQuery.error)
              : t((m) => m.pos.tableNotFound)
          }
          retryLabel={t((m) => m.common.retry)}
          onRetry={() => void gridQuery.refetch()}
        />
      </Screen>
    );
  }

  /*
   * EXACTLY ONE live bill: an ordinary table visit goes to it. The explicit
   * new-party action keeps this pre-order screen instead; visiting that screen
   * still creates nothing until a dish is selected.
   *
   * SEVERAL is the case B8 created, and it deliberately does NOT redirect. The
   * old comment here read "rather than offering to open a second one on the
   * same table" — and a second one on the same table is precisely what the
   * founder asked for. With more than one there is no "the" live invoice to
   * bounce to, and picking one would open a stranger's ticket; the picker below
   * lists them instead, with the ordinary pre-order screen still underneath so
   * a third party can be seated.
   *
   * Not while WE are the ones opening it: this redirect carries no item, and
   * the grid refetch that follows our own `openInvoice` would otherwise beat
   * `openWith`'s navigation and swallow the pick that started the order.
   */
  if (
    table.openInvoices.length === 1 &&
    !isOpening &&
    !(canOpen && searchParams.get("new") === "1")
  ) {
    return (
      <Navigate
        to={`/${locale}/pos/invoices/${table.openInvoices[0].invoiceId}`}
        replace
      />
    );
  }

  /**
   * THE moment a table becomes occupied: the first item the waiter picks.
   *
   * The item itself is not appended here — it is handed to the order view in
   * router state, which stages it exactly as a tap on its own menu would
   * (addon items open their picker there). So this opens the invoice and gets
   * out of the way; nothing reaches the kitchen until "send".
   */
  /**
   * Does this dish need asking before the bill is opened (B108)?
   *
   * The SAME rule the order screen applies to a handed-over dish — one
   * function, `handoffDecision`, so the two screens cannot disagree about which
   * dishes carry a question. `WAIT` means the menu or the groups have not come
   * back yet; the caller opens the modal on it and lets the loader hold the
   * waiter's place rather than guessing "no add-ons" and opening a bill.
   */
  const decideFor = (item: MenuItem) =>
    handoffDecision(
      item.id,
      menuQuery.data === undefined ? undefined : item,
      addonGroupsQuery.data,
    );

  const openWith = async (
    item: MenuItem,
    addonIds: string[] = [],
    /** PAPER: the picker's quantity — that many identical plates. */
    quantity = 1,
  ) => {
    if (!canOpen || isOpening) return;
    setOpening(true);
    try {
      // Only what was actually answered goes on the wire — see
      // `resolveOpenInvoicePayload` for the tri-state rule: an untouched
      // picker sends no `priceTierId` so the SERVER applies the auto tier
      // (and stays the one authority on the window); a deliberately chosen
      // "Base prices" sends explicit `null` so the server never overrides
      // that choice; a blank cover count sends no `guestCount` at all,
      // because `null` and `0` are different facts and the DTO refuses `0`
      // anyway.
      const result = await dispatchCommand({
        type: "openInvoice",
        payload: resolveOpenInvoicePayload({
          tableId: table.tableId,
          tierTouched,
          tierId,
          guestCountRaw: guestCount,
          captainTouched,
          captainSellerId: captainChosen,
        }),
      });
      // Navigate FIRST, refresh the floor after: the order view is what the
      // waiter is waiting for, and the grid is only stale for this screen,
      // which is on its way out.
      if (result.invoice) {
        // The PAPER register's section rides along, so the waiter lands on the
        // dishes they were picking from rather than back on the sections.
        const section = searchParams.get(SECTION_PARAM);
        navigate(
          `/${locale}/pos/invoices/${result.invoice.id}${
            section ? `?${SECTION_PARAM}=${encodeURIComponent(section)}` : ""
          }`,
          {
          replace: true,
          // B108: the ADD-ONS TRAVEL WITH THE DISH. They were chosen here,
          // before the bill existed, so the order screen stages the plate as
          // answered rather than opening a second picker over the same
          // question. `[]` on a plain dish, which is every tap that never met
          // a picker.
          state: {
            addProductId: item.id,
            addAddonIds: addonIds,
            ...(quantity > 1 ? { addQuantity: quantity } : {}),
          } satisfies InvoiceHandoff,
          },
        );
      }
      await invalidateInvoiceMutation(queryClient, result.invoice?.id);
      // D153 — the chip order is "most recently captained first", so a bill
      // that named one moves that waiter to the front for the next table.
      if (result.invoice?.captain) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.restaurantStaff,
        });
      }
    } catch (failure) {
      toast.error(errorMessage(failure));
    } finally {
      setOpening(false);
    }
  };

  const panelTables: PanelTable[] = tables.map((entry) => ({
    tableId: entry.tableId,
    name: entry.name,
    invoiceId: entry.openInvoices[0]?.invoiceId ?? null,
    openBillCount: entry.openInvoices.length,
  }));

  const pickTable = (picked: PanelTable) => {
    if (picked.openBillCount > 1) {
      navigate(`/${locale}/pos/tables/${picked.tableId}`);
      return;
    }
    if (picked.invoiceId) {
      navigate(`/${locale}/pos/invoices/${picked.invoiceId}`);
      return;
    }
    navigate(`/${locale}/pos/tables/${picked.tableId}`);
  };

  /*
   * Rendered through the panel's top slot so it sits where the order will be —
   * the waiter reads "this order at mall prices" as one thing. Absent entirely
   * when the store has no tiers: a picker with one option is furniture.
   */
  /*
   * THE LIVE BILLS ALREADY ON THIS TABLE (B8).
   *
   * Rendered only when there is MORE THAN ONE — a single bill redirects above,
   * and a free table has none — so this is exactly the case that could not
   * exist before unit 4: two families on one big table, or a table that turned
   * while the first cheque was still out.
   *
   * It sits ABOVE the pre-order screen rather than replacing it, because both
   * acts are live at once: joining a party that is already eating, and seating
   * a new one. Making the waiter choose a screen first would be inventing a
   * mode that the floor does not have.
   */
  const openBillsPicker =
    table.openInvoices.length > 1 ? (
      <div className="mb-3">
        <div className="mb-1.5 text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.06em] text-[color:var(--text-disabled)]">
          {/* QA3 — gated on `> 1`, so the dual is the smallest it shows. */}
          {t(
            countedKey(table.openInvoices.length, {
              one: (m) => m.pos.tableBillCountOne,
              two: (m) => m.pos.tableBillCountTwo,
              few: (m) => m.pos.tableBillCountFew,
              many: (m) => m.pos.tableBillCount,
            }),
            { count: table.openInvoices.length },
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          {table.openInvoices.map((bill) => (
            <button
              key={bill.invoiceId}
              type="button"
              data-testid={`open-bill-${bill.dailySequence}`}
              onClick={() =>
                navigate(`/${locale}/pos/invoices/${bill.invoiceId}`)
              }
              className="flex w-full items-center gap-2 rounded-[var(--radius-control)] border border-[color:var(--border-strong)] px-2.5 py-2 text-start text-[length:calc(var(--font-size-xs)_+_0.5px)] font-extrabold text-[color:var(--text)] transition-colors hover:border-[color:var(--accent)]"
            >
              <span>
                {t((m) => m.pos.invoiceNumber, {
                  sequence: bill.dailySequence,
                })}
              </span>
              <span className="flex-1" />
              <span className="tabular-nums">{money(bill.runningTotal)}</span>
            </button>
          ))}
        </div>
      </div>
    ) : null;
  /* A table that already carries bills opens on its panel (joining one is
     the likelier act); a session that may not open one has no menu at all. */
  const tablePane: TillPane = !canOpen
    ? "bill"
    : (paneOverride ?? (openBillsPicker ? "bill" : "menu"));

  const tierPicker =
    canOpen && !waiterSession && tiers.length > 0 ? (
      <div className="mb-2">
        <div className="mb-1.5 text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.06em] text-[color:var(--text-disabled)]">
          {t((m) => m.pos.tierPickerTitle)}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {[null, ...tiers.map((tier) => tier.id)].map((option) => {
            const tier = tiers.find((entry) => entry.id === option) ?? null;
            const isCurrent = effectiveTierId === option;
            // D47: a tier outside its hours is shown, not hidden — a waiter
            // asking "why can't I give the mall price?" needs to read the
            // hours, and a chip that vanished at 19:01 answers nothing. The
            // control is disabled instead, because the open command would
            // refuse the tap with a typed 400 anyway.
            const outOfWindow = tier !== null && !tier.isActiveNow;
            const windowLabel = tier ? formatTierWindow(t, tier) : null;
            return (
              <button
                key={option ?? "base"}
                type="button"
                aria-pressed={isCurrent}
                disabled={isOpening || outOfWindow}
                data-testid={`tier-${option ?? "base"}`}
                onClick={() => {
                  setTierTouched(true);
                  setTierId(option);
                }}
                className={[
                  "rounded-[var(--radius-control)] border-[length:var(--border-panel-width,1px)] px-2 py-[5px] text-[length:var(--font-size-2xs)] font-extrabold transition-colors",
                  isCurrent
                    ? "border-[color:var(--accent)] bg-[color:var(--accent)] text-[color:var(--accent-contrast)]"
                    : outOfWindow
                      ? "border-[color:var(--border)] text-[color:var(--text-disabled)]"
                      : "border-[color:var(--border-strong)] text-[color:var(--text-muted)] hover:text-[color:var(--text)]",
                ].join(" ")}
              >
                <span className="flex flex-col items-start gap-[1px]">
                  <span>
                    {tier
                      ? `${tier.name} −${tier.discountPercent}%`
                      : t((m) => m.pos.tierBase)}
                  </span>
                  {/* The hours, on the chip itself. Shown on every windowed
                      tier and not only the disabled ones: an in-window happy
                      hour that ends at 19:00 is exactly what a waiter taking a
                      18:55 order needs to see before the bill snapshots it. */}
                  {windowLabel ? (
                    <span className="text-[length:var(--font-size-2xs)] font-bold tabular-nums opacity-80">
                      {windowLabel}
                    </span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
        {/* Why a tier is already chosen when the waiter chose nothing. Without
            this line the picker looks pre-answered — the exact reading DEF-7
            spent a release removing from the void dialog. */}
        {autoTier && !tierTouched ? (
          <p className="mt-1.5 text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-muted)]">
            {t((m) => m.pos.tierAutoApplied)}
          </p>
        ) : null}
      </div>
    ) : null;

  /*
   * D45 (S5) / covers rework Phase 2a: covers, asked once, where the pricing
   * decision already is.
   *
   * It sits with the tier because both are captured at OPEN and neither can be
   * revised from this screen afterwards — one question, one moment, one place.
   * Optional in the strong sense: blank is stored as "nobody counted", and the
   * hint says so, because a cover count nobody counted is worse than none for
   * the average-spend report that reads it.
   *
   * SIX QUICK CHIPS, NOT A DIALOG: the common party sizes are one tap, nothing
   * is preselected on load (so skipping costs zero taps and leaves the field
   * genuinely blank), and tapping a selected chip again clears it — the same
   * "leave it blank" answer the hint promises, reachable without opening
   * anything. «أكتر» is the escape hatch past six, and it opens the SAME
   * dialog the invoice screen's covers row uses rather than a second bespoke
   * input.
   */
  const guestCountValue = Number.parseInt(guestCount, 10);
  const isCustomGuestCount =
    Number.isFinite(guestCountValue) &&
    guestCountValue > GUEST_COUNT_CHIP_VALUES[GUEST_COUNT_CHIP_VALUES.length - 1];
  const guestCountChipClass = (selected: boolean) =>
    [
      "flex h-9 min-w-[40px] items-center justify-center rounded-[var(--radius-control)] border-[length:var(--border-panel-width,1px)] px-2.5 text-[length:var(--font-size-sm)] font-extrabold tabular-nums transition-colors",
      selected
        ? "border-[color:var(--accent)] bg-[color:var(--accent)] text-[color:var(--accent-contrast)]"
        : "border-[color:var(--border-strong)] text-[color:var(--text-muted)] hover:text-[color:var(--text)]",
    ].join(" ");
  // PAPER: the covers are the panel head's button instead (founder,
  // 2026-10-03) — `headGuests` below, the same dialog, the same pending field.
  const guestCountField =
    canOpen && !paper ? (
      <div className="mb-2">
        <div className="mb-1.5 text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.06em] text-[color:var(--text-disabled)]">
          {t((m) => m.pos.guestCountLabel)}
        </div>
        <div
          className="flex flex-wrap gap-1.5"
          role="group"
          aria-label={t((m) => m.pos.guestCountLabel)}
        >
          {GUEST_COUNT_CHIP_VALUES.map((value) => {
            const selected = guestCountValue === value;
            return (
              <button
                key={value}
                type="button"
                aria-pressed={selected}
                aria-label={t(
                  countedKey(value, {
                    one: (m) => m.pos.guestCountWordOne,
                    two: (m) => m.pos.guestCountWordTwo,
                    few: (m) => m.pos.guestCountWordFew,
                    many: (m) => m.pos.guestCountWord,
                  }),
                  { count: value },
                )}
                disabled={isOpening}
                data-testid={`guest-count-chip-${value}`}
                onClick={() =>
                  setGuestCount(toggleGuestCountChip(guestCount, value))
                }
                className={guestCountChipClass(selected)}
              >
                {value}
              </button>
            );
          })}
          <button
            type="button"
            aria-pressed={isCustomGuestCount}
            disabled={isOpening}
            data-testid="guest-count-chip-more"
            onClick={() => setGuestCountDialogOpen(true)}
            className={guestCountChipClass(isCustomGuestCount)}
          >
            {isCustomGuestCount
              ? guestCountValue
              : t((m) => m.pos.guestCountMoreChip)}
          </button>
        </div>
        <p className="mt-1.5 text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-muted)]">
          {t((m) => m.pos.guestCountHint)}
        </p>
      </div>
    ) : null;

  /*
   * D153 — «الكابتن»: who owns this table. In BOTH tills (unlike the covers,
   * which the paper till moved into its head), because there is no head
   * control for it before the bill exists. Hidden while the team list is
   * loading or failed: the bill opens without a captain then, and the bill
   * head offers the change once it exists.
   */
  const captainField =
    canOpen && !waiterSession && staffQuery.data ? (
      <div className="mb-2" data-testid="open-captain">
        <div className="mb-1.5 text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.06em] text-[color:var(--text-disabled)]">
          {t((m) => m.captain.label)}
        </div>
        <CaptainChips
          chips={captainOptions}
          selected={shownCaptainId}
          disabled={isOpening}
          onPick={(sellerId) => {
            if (sellerId === null) return;
            setCaptainTouched(true);
            setCaptainChosen(toggleCaptainChip(shownCaptainId, sellerId));
          }}
        />
        {captainOptions.length > 0 ? (
          <p className="mt-1.5 text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-muted)]">
            {t((m) => m.captain.hint)}
          </p>
        ) : null}
      </div>
    ) : null;

  /*
   * The tiers query is `retry: false`, so a single failure is the final answer
   * and the picker simply never appears — which reads exactly like a store
   * with no tiers. The order can still be taken at base prices, so this says
   * that rather than blocking: a notice with a retry, in the same slot the
   * picker would have used.
   */
  const tierNotice =
    canOpen && !waiterSession && tiersQuery.isError ? (
      <ListCard tone="warning" className="mb-2" role="alert">
        <p className="text-sm">{t((m) => m.pos.tiersFailedNote)}</p>
        <Button
          variant="outline"
          size="touch"
          className="mt-3"
          onClick={() => void tiersQuery.refetch()}
          data-testid="tiers-retry"
        >
          {t((m) => m.common.retry)}
        </Button>
      </ListCard>
    ) : null;

  return (
    <Screen
      tone="dark"
      bleed
      title={paper ? t((m) => m.navPaper.floor) : table.name}
      leading={paper ? <PaperTillBarLeading /> : backLink}
      phoneHead={paper}
      /* Unit C — in the top bar, never inside a pane: it is about the TILL,
         not about this table's menu or its order (2026-09-21). */
      action={<DrawerStrip />}
    >
      {availability.confirm}
      {/* Below the desk layout the two panes stack, and the page scrolls as
          ONE: the table's own panel (guests, pricing, the empty-cart note)
          keeps its natural height, and so does the menu under it. Letting the
          menu flex had it squeezed to 36px in a phone's landscape, with no
          scroll at all (founder, 2026-10-06). */}
      {/* A plain column with the panel moved FIRST, not `flex-col-reverse`:
          a reversed column that scrolls starts at its END, which opened the
          page scrolled past the panel onto the middle of the menu. */}
      {/* Below `lg` the SAME shape as the invoice screen (waiter phone,
          2026-10-28): one pane at a time — the menu first, so a new table opens
          on its sections — and the till's bar under them, switching to this
          table's panel (guests, pricing, the bills already on it) and showing
          a 0 total and a greyed Send until the first dish opens the bill. A
          table that already carries bills opens on the panel: the waiter is
          more often joining one of them. It used to stack the panel over the
          menu in one scroll, with no bar — a second layout to learn. */}
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {/* No menu for a session that may not open a bill (founder,
            2026-10-06): the panel says why nothing can start here. */}
        {canOpen ? (
        <MenuBoard
          narrow={tablePane === "menu" ? "show" : "hide"}
          phoneLead={<TillPhoneLead name={table.name} meta={t((m) => m.pos.tableFree)} />}
          menu={menuQuery.data ?? []}
          categories={visibleCategories}
          countsByProductId={new Map()}
          disabled={isOpening}
          /* §5.8: 86'ing happens at the TILL, and the till is this screen too.
             Without it "we are out of fish" could not be recorded until
             somebody opened a cheque — at exactly the moment the kitchen
             shouts it, which is between tables. */
          {...availability.boardProps}
          onAdd={(item) => {
            if ((item.ingredientsMarkedOut ?? []).length > 0) {
              setWarnTarget(item);
              return;
            }
            /*
             * B108 — ASK BEFORE OPENING THE BILL.
             *
             * A dish with a size or a bread choice puts its picker up HERE,
             * and the invoice is opened only if the waiter confirms it. The
             * old order — open the bill, then ask on the next screen — meant a
             * cancelled picker left a real 0 EGP invoice holding the table.
             *
             * The `WAIT` arm is why the picker opens rather than the bill: the
             * groups may still be in flight, and "I do not know yet whether
             * this dish needs asking" must not be resolved by guessing. The
             * modal shows its loader until the answer arrives and then becomes
             * the picker, or closes itself and opens the bill.
             */
            if (decideFor(item) === "ADD") {
              void openWith(item);
              return;
            }
            setPickerItem(item);
          }}
          // The tier is chosen HERE, before the bill exists, so the grid has to
          // follow the picker above it: choosing "خصم المول −20%" and still
          // reading catalogue prices off the tiles is the same mismatch the
          // invoice screen had, one step earlier. The first tap opens the bill
          // ON this tier, so the number shown is the number that will land.
          discountPercent={
            tiers.find((entry) => entry.id === effectiveTierId)
              ?.discountPercent ?? 0
          }
          // PAPER: the register's tabs and crumbs, and "from" on a dish that
          // asks for a size or extras first.
          paperHead={paper ? <PaperRegisterTabs /> : undefined}
          fromPrice={(item) =>
            quoteFromPrice(
              item.price,
              tiers.find((entry) => entry.id === effectiveTierId)
                ?.discountPercent ?? 0,
              resolveGroupsForItem(
                item,
                new Map((addonGroupsQuery.data ?? []).map((g) => [g.id, g])),
              ),
            )
          }
        />
        ) : null}

        <OrderPanel
          narrow={tablePane === "bill" ? "show" : "hide"}
          invoice={null}
          platformName={null}
          sentItems={[]}
          drafts={[]}
          /*
           * B117/RT-C. UNREACHABLE here and supplied anyway: this panel is the
           * EMPTY table — no invoice, no plates — so nothing can be remade and
           * nothing can be refused. The prop stays REQUIRED on `OrderPanel`
           * rather than optional precisely because the defect it closed was a
           * refusal that told the waiter nothing, and an optional prop is how a
           * surface silently grows that silence back.
           */
          onRemakeBlocked={() =>
            toast.info(t((m) => m.serverErrors.itemNotStarted))
          }
          pending={
            openBillsPicker ||
            tierNotice ||
            tierPicker ||
            guestCountField ||
            captainField ? (
              <>
                {/* The live bills FIRST: a waiter arriving at a table with two
                    families on it is more often joining one than seating a
                    third, and the pre-order screen is still right below. */}
                {openBillsPicker}
                {tierNotice}
                {tierPicker}
                {guestCountField}
                {captainField}
              </>
            ) : null
          }
          conflict={null}
        /* No invoice, so no bill and nothing to claim to have paid (D44). */
        dinerPayment={null}
          closeBlocking={NO_CLOSE_BLOCKING}
          bindLabel={t((m) => m.pos.gridTitle)}
          tableName={table.name}
          canStart={canOpen}
          /* This screen IS a table, and it renders no settled overlay (it
             passes `invoice: null`), so both land on the floor. */
          onNewOrder={() => navigate(`/${locale}/pos`)}
          onBackToTables={() => navigate(`/${locale}/pos`)}
          tables={panelTables}
          currentTableId={table.tableId}
          onPickTable={pickTable}
          /* PAPER head: the pending cover count, sent by the open command
             (`guestCountRaw`) — blank stays "nobody counted" (D45). Only for a
             session that may open the bill, as the chips were. */
          headGuests={
            paper && canOpen
              ? {
                  count:
                    Number.isFinite(guestCountValue) && guestCountValue > 0
                      ? guestCountValue
                      : null,
                  onOpen: () => setGuestCountDialogOpen(true),
                  disabled: isOpening,
                }
              : undefined
          }
          busyKey={null}
          isBusy={isOpening}
          canAppend={false}
          canFire={false}
          canServe={false}
          canPrepare={false}
          canPrint={false}
          canVoid={false}
          canRemake={false}
          /* Nothing on this screen is on a bill yet, so there is no plate to
             amend the swaps of (B19) — stated rather than defaulted, the same
             way every other capability here is. */
          canSubstitute={false}
          kitchenPrintsChits={false}
          canClose={false}
          canSettle={false}
          canCancel={false}
          onCancel={() => {}}
          onReopen={() => {}}
          /* The pre-order view has no invoice at all, so there is nothing to
             refund and no settled overlay to hang the control on. Stated
             rather than left to a default, the same way `canCancel` is. */
          canRefund={false}
          onRefund={() => {}}
          /* B89 — the same reason `canRefund` is false here: this screen
             passes `invoice: null`, so there is no tender that could have
             over-shot a total. Stated
             rather than left to a default; unit 4 shipped them MISSING, which
             `tsc -b` reported and nothing else did. */
          onRefundOverpayment={() => {}}
          canTransfer={false}
          onTransfer={() => {}}
          /* B107 — this screen passes `invoice: null`, so there is no bill and
             no customer to correct. Stated rather than defaulted, the same way
             `canTransfer` above is. */
          canEditCustomer={false}
          onEditCustomer={() => {}}
          canEditCharges={false}
          onEditCharges={() => {}}
          onEditNote={() => {}}
          onFireGroup={() => {}}
          onDraftPlus={() => {}}
          onDraftMinus={() => {}}
          onDraftRemove={() => {}}
          onSentPlus={() => {}}
          onSentVoid={() => {}}
          onAmendSwaps={() => {}}
          onRemake={() => {}}
          onServe={() => {}}
          onStartPreparing={() => {}}
          onMarkReady={() => {}}
          onPrintChit={() => {}}
          onFire={() => {}}
          onClose={() => {}}
          onSettle={() => {}}
          onPrint={() => window.print()}
        />
      </div>
      <TillSummaryBar
        pane={tablePane}
        placeName={table.name}
        onTogglePane={() => setPaneOverride(tablePane === "menu" ? "bill" : "menu")}
        runningTotal={0}
        lineCount={0}
        primary={{
          kind: canSendToKitchen(session, settingsQuery.data?.ordersViaCashier)
            ? "fire"
            : "requestCashier",
          enabled: false,
          permitted: true,
        }}
        close={null}
        isBusy={isOpening}
        busyKey={null}
        onPrimary={() => {}}
        onClose={() => {}}
      />

      {/* B108 — THE PICKER, BEFORE THE BILL.

          Two states behind one modal, and the loader is the point: the waiter
          has already tapped, so something has to be on screen holding their
          place while the addon groups arrive. Opening nothing would read as a
          dead tap and get tapped again; opening the BILL — what this used to do
          — left a 0 EGP invoice on the table when they backed out.

          When the answer lands as "no add-ons after all", the modal closes
          itself and the bill opens, so a plain dish that happened to be tapped
          during a slow load costs one flicker rather than a wrong question. */}
      {pickerItem && decideFor(pickerItem) === "WAIT" ? (
        <Modal
          isOpen
          onClose={() => setPickerItem(null)}
          size="sm"
          /* `hideFooter` is a DECISION here, not residue from before UX-round D10: this
             «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
             dialog is a LOADING placeholder with no action to take — the X is
             the only way out because there is nothing else to offer yet. */
          hideFooter
          title={localName(pickerItem)}
          closeLabel={t((m) => m.common.close)}
          data-testid="addon-loading"
        >
          <div className="flex flex-col gap-2 py-2">
            <LoadingSkeleton rows={3} />
            <p className="text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--text-muted)]">
              {t((m) => m.pos.addonLoading)}
            </p>
          </div>
        </Modal>
      ) : null}

      {pickerItem && decideFor(pickerItem) === "PICKER" ? (
        <AddonPicker
          item={pickerItem}
          onMarkSoldOut={
            paper && availability.boardProps.onToggleAvailability
              ? () => {
                  const item = pickerItem;
                  setPickerItem(null);
                  availability.boardProps.onToggleAvailability?.(item);
                }
              : undefined
          }
          groups={resolveGroupsForItem(
            pickerItem,
            new Map((addonGroupsQuery.data ?? []).map((g) => [g.id, g])),
          )}
          /* The tier chosen on this screen, so the picker quotes what the plate
             will cost on the bill it is about to open — the same reason the
             menu grid below it follows the picker. */
          discountPercent={
            tiers.find((entry) => entry.id === effectiveTierId)
              ?.discountPercent ?? 0
          }
          // PAPER: the dish's quantity rides to the bill with the answers.
          withQuantity={paper}
          onCancel={() => setPickerItem(null)}
          onConfirm={(addonIds, quantity) => {
            const item = pickerItem;
            setPickerItem(null);
            // NOW the bill opens, carrying the answers. Nothing was written
            // while the question was on screen, so a cancel above leaves the
            // table exactly as free as it was.
            if (item) void openWith(item, addonIds, quantity);
          }}
        />
      ) : null}

      {/* B18 — the same dialog the invoice grid uses, so the two add paths
          cannot warn about one plate in two different ways. */}
      <IngredientWarningDialog
        item={warnTarget}
        locale={locale}
        onCancel={() => setWarnTarget(null)}
        onConfirm={() => {
          const item = warnTarget;
          setWarnTarget(null);
          // Through the SAME gate as an ordinary tap (B108): a dish that is
          // 86'd AND carries a size still has to be asked about before the bill
          // is opened, or this path keeps the defect the other one lost.
          if (!item) return;
          if (decideFor(item) === "ADD") {
            void openWith(item);
            return;
          }
          setPickerItem(item);
        }}
      />

      {/* Covers rework Phase 2a — «أكتر»'s escape hatch past the six chips.
          The SAME dialog the invoice screen's covers row opens; here it
          writes straight into local state rather than dispatching a command
          — the bill does not exist yet, so there is nothing to write to but
          the field `openWith` reads when it finally opens one. */}
      {guestCountDialogOpen ? (
        <GuestCountDialog
          current={
            Number.isFinite(guestCountValue) && guestCountValue > 0
              ? guestCountValue
              : null
          }
          isSubmitting={false}
          onCancel={() => setGuestCountDialogOpen(false)}
          onConfirm={(value) => {
            setGuestCount(value === null ? "" : String(value));
            setGuestCountDialogOpen(false);
          }}
        />
      ) : null}
    </Screen>
  );
}
